/**
 * 🏷️ VERIFICATION STATUS BADGE - Enhanced status display with proper colors and labels
 */

import React from 'react';
import { Chip, Box, Typography, Tooltip } from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Warning,
  Error,
  Edit,
  Assignment,
  VerifiedUser,
} from '@mui/icons-material';

export type VerificationStatusType =
  | 'approved'
  | 'pending_review'
  | 'rejected'
  | 'draft'
  | 'expired'
  | 'needs_documents'
  | 'verified_driver'
  | 'pending_nin'
  | 'complete'
  | 'not_started'
  | 'nin_completed'
  | 'documents_uploaded';

interface VerificationStatusBadgeProps {
  status: VerificationStatusType;
  completionPercentage?: number;
  type?: 'driver' | 'identity' | 'general';
  showPercentage?: boolean;
  size?: 'small' | 'medium';
}

export const VerificationStatusBadge: React.FC<VerificationStatusBadgeProps> = ({
  status,
  completionPercentage = 0,
  type = 'general',
  showPercentage = true,
  size = 'small'
}) => {
  const getStatusConfig = (status: VerificationStatusType, type: string) => {
    const configs = {
      // Approved states
      'approved': {
        label: type === 'driver' ? 'Verified Driver' : 'Approved',
        color: 'success' as const,
        icon: <VerifiedUser />,
        bgColor: 'success.main',
        textColor: 'white'
      },
      'verified_driver': {
        label: 'Verified Driver',
        color: 'success' as const,
        icon: <VerifiedUser />,
        bgColor: 'success.main',
        textColor: 'white'
      },
      'complete': {
        label: type === 'driver' ? 'Driver Application Complete' : 'Complete',
        color: 'success' as const,
        icon: <CheckCircle />,
        bgColor: 'success.main',
        textColor: 'white'
      },

      // Pending states  
      'pending_review': {
        label: 'Pending Review',
        color: 'warning' as const,
        icon: <Schedule />,
        bgColor: 'warning.main',
        textColor: 'white'
      },
      'pending_nin': {
        label: 'Pending Identity Verification',
        color: 'warning' as const,
        icon: <Assignment />,
        bgColor: 'warning.main',
        textColor: 'white'
      },
      'not_started': {
        label: 'Not Started',
        color: 'default' as const,
        icon: <Assignment />,
        bgColor: 'grey.400',
        textColor: 'white'
      },
      'nin_completed': {
        label: 'NIN Verified',
        color: 'info' as const,
        icon: <CheckCircle />,
        bgColor: 'info.main',
        textColor: 'white'
      },
      'documents_uploaded': {
        label: 'Pending Review',
        color: 'warning' as const,
        icon: <Schedule />,
        bgColor: 'warning.main',
        textColor: 'white'
      },
      'draft': {
        label: 'In Progress',
        color: 'info' as const,
        icon: <Edit />,
        bgColor: 'info.main',
        textColor: 'white'
      },

      // Problem states
      'needs_documents': {
        label: 'Needs Documents',
        color: 'error' as const,
        icon: <Warning />,
        bgColor: 'error.main',
        textColor: 'white'
      },
      'rejected': {
        label: 'Rejected',
        color: 'error' as const,
        icon: <Error />,
        bgColor: 'error.main',
        textColor: 'white'
      },
      'expired': {
        label: 'Expired',
        color: 'error' as const,
        icon: <Error />,
        bgColor: 'error.main',
        textColor: 'white'
      },
    };

    return configs[status] || configs['draft'];
  };

  const config = getStatusConfig(status, type);

  const formatPercentage = (percentage: number) => {
    if (percentage === 100) return '100%';
    if (percentage >= 90) return `${Math.round(percentage)}%`;
    if (percentage > 0) return `${Math.round(percentage)}%`;
    return '0%';
  };

  const getTooltipText = () => {
    const baseText = config.label;
    if (showPercentage && completionPercentage > 0) {
      return `${baseText} - ${formatPercentage(completionPercentage)} Complete`;
    }
    return baseText;
  };

  return (
    <Tooltip title={getTooltipText()} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip
          icon={config.icon}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {config.label}
              </Typography>
              {showPercentage && completionPercentage > 0 && (
                <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.9 }}>
                  ({formatPercentage(completionPercentage)})
                </Typography>
              )}
            </Box>
          }
          size={size}
          color={config.color}
          variant={status === 'approved' || status === 'verified_driver' || status === 'complete' ? 'filled' : 'outlined'}
          sx={{
            '& .MuiChip-icon': {
              fontSize: size === 'small' ? 14 : 16
            },
            ...(status === 'approved' || status === 'verified_driver' || status === 'complete' ? {
              backgroundColor: config.bgColor,
              color: config.textColor,
              '& .MuiChip-icon': {
                color: config.textColor
              }
            } : {})
          }}
        />
      </Box>
    </Tooltip>
  );
};

/**
 * Get proper status from backend data
 * FIX: Uses verification_stage instead of identity_verified to show accurate progress
 */
export const getVerificationStatusFromUser = (user: any): {
  status: VerificationStatusType;
  percentage: number;
  type: 'driver' | 'identity' | 'general';
} => {
  // Check driver verification first
  if (user.driverVerification) {
    const driverStatus = user.driverVerification.status;
    const percentage = user.driverVerification.completionPercentage || 0;

    // Map backend status to our status types
    if (driverStatus === 'approved' && percentage === 100) {
      return { status: 'verified_driver', percentage: 100, type: 'driver' };
    }
    if (driverStatus === 'pending_review' && percentage === 100) {
      return { status: 'complete', percentage: 100, type: 'driver' };
    }
    if (driverStatus === 'pending_review') {
      return { status: 'pending_review', percentage, type: 'driver' };
    }
    if (driverStatus === 'rejected') {
      return { status: 'rejected', percentage, type: 'driver' };
    }
    if (driverStatus === 'draft') {
      return { status: 'draft', percentage, type: 'driver' };
    }
  }

  // ✅ FIX: Use verification_stage (most accurate) instead of is_verified
  if (user.verification_stage || user.verificationStage) {
    const stage = user.verification_stage || user.verificationStage;

    switch (stage) {
      case 'not_started':
        return { status: 'not_started', percentage: 0, type: 'identity' };

      case 'nin_completed':
        return { status: 'nin_completed', percentage: 33, type: 'identity' };

      case 'documents_uploaded':
        return { status: 'documents_uploaded', percentage: 66, type: 'identity' };

      case 'completed':
        return { status: 'approved', percentage: 100, type: 'identity' };

      default:
        // Unknown stage - fallback
        break;
    }
  }

  // Fallback to is_verified (for backward compatibility)
  if (user.isVerified || user.is_verified) {
    return { status: 'approved', percentage: 100, type: 'identity' };
  }

  // Check identity_verified (this only means NIN is done, not full verification)
  if (user.identity_verified || user.identityVerified) {
    // If they have identity_verified but no verification_stage, assume NIN completed
    return { status: 'nin_completed', percentage: 33, type: 'identity' };
  }

  // Check if it's an identity verification pending case
  if (user.userType === 'driver' && !(user.isVerified || user.is_verified)) {
    return { status: 'pending_nin', percentage: 0, type: 'identity' };
  }

  // Default not started state
  return { status: 'not_started', percentage: 0, type: 'general' };
};